import React, { useState } from 'react';
import type { CustomerContext, Session } from '../session.js';
import { Button } from './ui/button.js';
import { verificationStatusLabel } from '../lib/verification-status.js';
import { cliMatchLabel } from '../lib/cli-match.js';
import { onlineCardDisplay } from '../lib/online-card.js';
import ptsbLogo from '../assets/ptsb-logo.png';
import { DEPLOYED_VERSION } from '../lib/build-info.js';

/**
 * The deployed version ({@link DEPLOYED_VERSION} — `v<app version>` from the
 * committed `app/VERSION`, plus the build commit when stamped) is surfaced as the
 * PTSB logo tooltip so anyone can see exactly which version is deployed by hovering
 * the logo (it is also logged to the console at startup). See docs/versioning.md.
 *
 * #605 — the tooltip also carries the signed-in agent's current RBAC role
 * (`Role: <role>`) alongside the version, so an agent can confirm which role they
 * are operating under by hovering the logo. The role is read from the session; when
 * there is no session in context (e.g. the logo-only Admin banner) only the version
 * is shown.
 */
function logoTitle(role: string | undefined): string {
  const base = `PTSB Agent Desktop — ${DEPLOYED_VERSION}`;
  return role ? `${base} — Role: ${role}` : base;
}

interface AgentBannerProps {
  session: Session | undefined;
  onClearSession: () => void;
  /**
   * Admin Console banner strip (#302 follow-up, 2026-07-09 — Dylan: on the
   * Admin Console page ONLY, the banner shows ONLY the logo — no customer
   * information, no interaction-state pill, no Clear session button). Every
   * other screen keeps the full banner (default `false`).
   */
  logoOnly?: boolean;
  /**
   * #430 — when true, show the prominent "Connect to Genesys" button in the
   * banner (next to the Online/Offline pill). Shown whenever the app is
   * Genesys-capable but not yet linked — INCLUDING offline, so an agent can
   * pre-heat the connection before going on queue. Clicking runs the first-party
   * popup PKCE link ({@link onConnectGenesys}).
   */
  showConnectGenesys?: boolean;
  /**
   * #430 — pulse the Connect button to draw the eye ONLY on a live (Online)
   * interaction with no link yet (a call needs enrichment now). Offline it is a
   * calm, steady button (no animation).
   */
  connectGenesysPulse?: boolean;
  /** #430 — opens the Genesys link popup (from the banner Connect button). */
  onConnectGenesys?: () => void;
  /**
   * #715 — when provided, renders a discrete "Log out" link directly beneath
   * the Clear session button. Only supplied on the ID&V and Customer Search
   * pages (see App.tsx). Navigates to GET /auth/logout (RP-initiated logout —
   * destroys the server-side session and redirects to the IdP logout endpoint).
   */
  onLogout?: () => void;
}

/**
 * A single labelled banner field (CCF-3/CCF-12).
 *
 * Empty fields never render blank: before a customer is identified the banner
 * reads **"N/A"** (`fallback="N/A"`); once a customer is in context an
 * individually-missing field reads **"Not available"**.
 */
function BannerField({
  label,
  value,
  fallback,
}: {
  label: string;
  value?: string | number;
  fallback: string;
}): React.JSX.Element {
  const display =
    value === undefined || value === null || value === ''
      ? fallback
      : String(value);
  return (
    <p className="font-['Arial',sans-serif] text-[14px] text-white m-0">
      <span className="font-bold">{label}:</span> {display}
    </p>
  );
}

/**
 * Persistent agent banner.
 *
 * Faithful port of the PTSB design-system top banner (the export's `NavDark`):
 * the PTSB logo, the customer/verification fields (Name, Online card number,
 * CIF, Assistance required, Customer verification, CLI match, Calls in last
 * week) split across vertical dividers, the live Online/Offline state, and the
 * orange Clear session button.
 *
 * Fields are driven from the session's customer context (placeholder values
 * until a real customer is in context); the button delegates to `onClearSession`.
 */
export function AgentBanner({
  session,
  onClearSession,
  logoOnly = false,
  showConnectGenesys = false,
  connectGenesysPulse = false,
  onConnectGenesys,
  onLogout,
}: AgentBannerProps): React.JSX.Element {
  // CCF-11 (#520) — toggle to show/hide the acknowledged reasons in the banner
  // reminder row. Must be declared before any early return (React hooks rules).
  const [showStopAndThinkReasons, setShowStopAndThinkReasons] = useState(false);

  // CCF-10 (#766) — "Clear Session" must first show a Yes/No confirmation
  // ("Are you sure you want to clear down this session?") before tearing down
  // the customer's authentication session. The Clear session button opens this
  // confirmation; only "Yes" delegates to `onClearSession` (which performs the
  // existing clear-and-navigate-to-ID&V behaviour), while "No" simply closes
  // the dialog. Declared before the `logoOnly` early return (React hooks rules).
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Admin Console (#302 follow-up, 2026-07-09 — Dylan: strip the banner down
  // to ONLY the logo on that page): short-circuit before touching any
  // customer/verification/state derivations below — there is no customer
  // context, interaction state, or Clear-session action on this page.
  if (logoOnly) {
    return (
      <header
        className="bg-[#1d252c] px-12 py-0"
        role="banner"
        aria-label="Agent banner"
      >
        <div className="max-w-[1800px] mx-auto h-[101px]">
          <div className="flex items-center h-[101px]">
            <div
              className="h-[44px] w-[100px] flex items-center justify-center"
              title={logoTitle(session?.role)}
              aria-label="PTSB Logo"
            >
              <img
                alt="PTSB"
                className="max-w-full max-h-full object-contain"
                src={ptsbLogo}
              />
            </div>
          </div>
        </div>
      </header>
    );
  }

  const state = session?.state ?? 'offline';
  const c: CustomerContext = session?.customer ?? {};

  // CCF-3/CCF-12: before any customer is identified (no CIF/name/card), the
  // customer fields read "N/A"; once a customer is in context an individually
  // missing field reads "Not available". Calls-in-last-week is call-derived and
  // may be populated pre-auth, so it always uses "Not available".
  const preAuth = !c.cif && !c.name && !c.onlineCardNumber;
  const fb = preAuth ? 'N/A' : 'Not available';

  // CCF-2: the verification field reflects the customer's CURRENT verification
  // level (Not Verified → PAN Verified → Medium/High Security Verified) derived
  // from the live `securityLevel`, not the raw gateway Y/N flag. Undefined
  // pre-ID&V so the field falls back to "N/A".
  //
  // CCF-101 (#419, Dylan 2026-07-17 — AUTHORITATIVE): on an OFFLINE journey
  // there is no live customer to verify and no ID&V/High-Security gate — the
  // security state is treated as High-Security-complete in the background (see
  // #412 for the read-side gate scoping), so pages render without a forced
  // return to High Security. But the banner must NOT imply a verification the
  // agent did not perform: on ALL offline journeys the verification field
  // displays simply "Offline" (not "Medium Security Verified", not "Not
  // Verified"). Online-journey behaviour is unchanged.
  const verificationStatus =
    state === 'offline' ? 'Offline' : verificationStatusLabel(c);

  // CCF-3 (#36): the "CLI match" field is only meaningful during a live call.
  // Offline (no live interaction) → "N/A" always; Online → "Yes" when the
  // caller's CLI matches the mobile number on file, otherwise "N". Derived from
  // the live interaction state, not the raw gateway flag, so it cannot show a
  // stale Y/N outside an active call.
  const cliMatch = cliMatchLabel(state, c.cliMatch);

  return (
    <header
      className="bg-[#1d252c] px-12 py-0"
      role="banner"
      aria-label="Agent banner"
    >
      <div className="max-w-[1800px] mx-auto h-[101px]">
        <div className="flex items-center justify-between gap-8 h-[101px]">
          {/* Logo */}
          <div className="flex items-center gap-6">
            <div
              className="h-[44px] w-[100px] flex items-center justify-center"
              title={logoTitle(session?.role)}
              aria-label="PTSB Logo"
            >
              <img
                alt="PTSB"
                className="max-w-full max-h-full object-contain"
                src={ptsbLogo}
              />
            </div>
          </div>

          {/* Customer information */}
          <div className="flex gap-8 flex-1">
            <div className="flex flex-col gap-2">
              <BannerField label="Name" value={c.name} fallback={fb} />
              <BannerField
                label="Online card number"
                // #191: the search journey binds the gateway's full 16-digit
                // `OnlineCardNbr` (a `503585` BIN + the 10-digit Open24 number,
                // #77). The banner shows the Open24 number — its trailing 10
                // digits, matching the dashboard figma — not the full 16.
                value={
                  c.onlineCardNumber === undefined
                    ? undefined
                    : onlineCardDisplay(c.onlineCardNumber)
                }
                fallback={fb}
              />
            </div>

            <div className="h-[60px] w-px bg-white/30" />

            <div className="flex flex-col gap-2">
              <BannerField label="CIF" value={c.cif} fallback={fb} />
              <BannerField
                label="Assistance required"
                value={c.assistanceRequired}
                fallback={fb}
              />
            </div>

            <div className="h-[60px] w-px bg-white/30" />

            <div className="flex flex-col gap-2">
              <BannerField
                label="Customer verification"
                value={verificationStatus}
                fallback={fb}
              />
              <BannerField label="CLI match" value={cliMatch} fallback={fb} />
            </div>

            <div className="h-[60px] w-px bg-white/30" />

            <div className="flex flex-col gap-2 h-[60px] justify-start">
              <BannerField
                label="Calls in last week"
                value={c.callsInLastWeek}
                fallback="Not available"
              />
            </div>
          </div>

          {/* State + actions */}
          <div className="flex items-center gap-4">
            {/* #430 — prominent "Connect to Genesys" affordance, shown next to
                the state pill when a live interaction's Genesys token is not yet
                linked. First-party popup PKCE link (browsers need a gesture, so
                this is a real button). Hidden once linked. */}
            {showConnectGenesys && (
              <Button
                type="button"
                onClick={onConnectGenesys}
                aria-label="Connect to Genesys"
                data-testid="connect-genesys"
                data-pulse={connectGenesysPulse}
                className={`bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-4 py-2 h-auto font-['Arial',sans-serif] font-bold text-[14px]${
                  connectGenesysPulse ? ' animate-pulse' : ''
                }`}
              >
                Connect to Genesys
              </Button>
            )}
            <span
              data-testid="interaction-state"
              data-state={state}
              className={`px-3 py-1 rounded-full text-[13px] font-bold font-['Arial',sans-serif] ${
                state === 'online'
                  ? 'bg-[#d1fae5] text-[#065f46]'
                  : 'bg-white/15 text-white'
              }`}
              aria-label={`Interaction state: ${state}`}
            >
              {state === 'online' ? 'Online' : 'Offline'}
            </span>
            <div className="flex flex-col items-center gap-1">
              <Button
                type="button"
                onClick={() => setShowClearConfirm(true)}
                aria-label="Clear session"
                className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
              >
                Clear session
              </Button>
              {onLogout && (
                <button
                  type="button"
                  onClick={onLogout}
                  aria-label="Log out"
                  className="text-white/60 hover:text-white/90 text-[12px] font-['Arial',sans-serif] underline bg-transparent border-0 p-0 cursor-pointer"
                >
                  Log out
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* CCF-11 (#520) — persistent Stop and Think reminder row. Shown on ALL
          screens after the agent has acknowledged the alert on ID&V, so they
          cannot forget the fraud advisory is active for this call. Reasons are
          initially collapsed (the alert was already reviewed); a toggle lets
          the agent re-read them without returning to ID&V. */}
      {c.stopAndThinkAcknowledged && (
        <div className="max-w-[1800px] mx-auto pb-3">
          <div
            data-testid="stop-and-think-banner"
            className="flex items-start gap-4 px-6 py-3 bg-[#FFF6F2] border border-[#D23A06] rounded-[4px]"
          >
            <div className="flex flex-col gap-1 flex-1">
              <span className="text-[14px] font-['Arial',sans-serif] text-[#D23A06]">
                <span className="font-bold">Attention Required:</span> Stop and
                Think acknowledged
                {(c.stopAndThinkReasons?.length ?? 0) > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowStopAndThinkReasons((v) => !v)}
                    className="ml-3 text-[13px] font-['Arial',sans-serif] text-[#D23A06] underline cursor-pointer bg-transparent border-0 p-0"
                    aria-expanded={showStopAndThinkReasons}
                  >
                    {showStopAndThinkReasons ? 'Hide reasons' : 'Show reasons'}
                  </button>
                )}
              </span>
              {showStopAndThinkReasons &&
                (c.stopAndThinkReasons?.length ?? 0) > 0 && (
                  <p className="text-[13px] font-['Arial',sans-serif] text-[#1d252c] m-0 leading-relaxed">
                    <span className="font-bold">Reasons:</span>{' '}
                    {c.stopAndThinkReasons?.join(', ')}
                  </p>
                )}
            </div>
          </div>
        </div>
      )}
      {/* CCF-10 (#766) — Clear Session confirmation. Y/N only, no other action:
          "Yes" performs the clear (delegates to onClearSession, which clears the
          banner except "Calls in last week" and navigates to ID&V); "No" closes
          the dialog and leaves the session untouched. */}
      {showClearConfirm && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="clear-session-confirm-title"
          data-testid="clear-session-confirm"
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
        >
          <div className="bg-white border-2 border-[#dad7d3] shadow-lg max-w-[480px] w-full p-8">
            <h2
              id="clear-session-confirm-title"
              className="text-[20px] font-['Arial',sans-serif] font-bold m-0 mb-6 text-[#1d252c]"
            >
              Are you sure you want to clear down this session?
            </h2>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                aria-label="No, do not clear session"
                data-testid="clear-session-confirm-no"
                className="bg-white text-[#1D252C] border-2 border-[#1D252C] hover:bg-[#f2f2f2] rounded-md px-6 py-2 h-auto font-['Arial',sans-serif] font-bold text-[15px]"
              >
                No
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowClearConfirm(false);
                  onClearSession();
                }}
                aria-label="Yes, clear session"
                data-testid="clear-session-confirm-yes"
                className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-2 h-auto font-['Arial',sans-serif] font-bold text-[15px]"
              >
                Yes
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
